import {
  AimOutlined,
  ArrowLeftOutlined,
  CameraOutlined,
  CheckCircleFilled,
  CloseCircleOutlined,
  ExclamationCircleFilled,
  FileImageOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SmileOutlined,
  ThunderboltOutlined,
  UserOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { Alert, Button, Checkbox, Empty, InputNumber, Select, Skeleton, Tag, Tooltip, message } from 'antd'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  authorizeVirtualIpRealPerson,
  dispatchVirtualIpCharacterBatch,
  getVirtualIpCharacterBatch,
  getVirtualIpCharacterContext,
  getVirtualIpDraft,
  retryVirtualIpCharacterBatch,
  saveVirtualIpCharacterDraft,
  type SaasApiError,
  type VirtualIpCharacterBatch,
  type VirtualIpCharacterContext,
  type VirtualIpCharacterReferenceAsset,
  type VirtualIpCharacterSourceMode,
  type VirtualIpDraft,
  type VirtualIpReferenceRole,
  type VirtualIpRealPersonAngle,
  type VirtualIpRealPersonPhoto,
  resolveSaasMediaUrl,
} from '../../api'
import { ImageAssetPicker, ImageAssetPickerField } from '../../components/ImageAssetPicker'
import { useWorkspaceState } from '../WorkspaceContext'
import VirtualIpStepper from './VirtualIpStepper'
import VirtualIpCharacterWorkbench from './VirtualIpCharacterWorkbench'
import { useResolvedVirtualIpProgress } from './useResolvedVirtualIpProgress'
import { useVirtualIpWorkflowContext } from './VirtualIpWorkflowContext'
import { VIRTUAL_IP_FLOW_TOTAL_STEPS } from './virtualIpFlow'
import './virtualIp.css'

/** VISTOERA_VIRTUAL_IP_STEP5_V1 — "打造你的 IP 形象".
 *
 * Architecture (task spec Q2/PC2-PC3 ownership): this page never talks
 * to ChatGPT/Multi AI Chat directly. It only calls the STEP5 backend
 * surface (`saas_virtual_ip_character.py`), which itself dispatches the
 * provider-neutral `virtual_ip_character_generation` Skill through the
 * same `HarnessRunService` STEP2/STEP3 already use — no second AI
 * calling system, no page-level provider awareness.
 *
 * Readiness is never hardcoded here — `UPSTREAM_POSITIONING_READY` and
 * `AI_EXECUTION_READY` are both read verbatim from the backend's
 * `resolve_readiness()` on every load/poll; the generation CTA is gated
 * on the real value, never assumed true. */

type PageState = 'loading' | 'error' | 'upstream_not_ready' | 'ready'

const POLL_INTERVAL_MS = 4000
const IN_FLIGHT_STATUSES = new Set(['QUEUED', 'RUNNING', 'WAITING_PROVIDER', 'RESULT_RECEIVED'])

const REFERENCE_ROLE_OPTIONS: { value: VirtualIpReferenceRole; labelKey: string }[] = [
  { value: 'IDENTITY', labelKey: 'step5.referenceTypes.IDENTITY' },
  { value: 'STYLE', labelKey: 'step5.referenceTypes.STYLE' },
  { value: 'PRODUCT', labelKey: 'step5.referenceTypes.PRODUCT' },
  { value: 'COLOR', labelKey: 'step5.referenceTypes.COLOR' },
  { value: 'CLOTHING', labelKey: 'step5.referenceTypes.CLOTHING' },
  { value: 'SCENE', labelKey: 'step5.referenceTypes.SCENE' },
  { value: 'OTHER', labelKey: 'step5.referenceTypes.OTHER' },
]

const REAL_PERSON_ANGLES: { value: VirtualIpRealPersonAngle; labelKey: string; requirement: 'REQUIRED' | 'RECOMMENDED' | 'OPTIONAL' }[] = [
  { value: 'front', labelKey: 'step5.photoAngles.front', requirement: 'REQUIRED' },
  { value: 'left_45', labelKey: 'step5.photoAngles.left_45', requirement: 'RECOMMENDED' },
  { value: 'right_45', labelKey: 'step5.photoAngles.right_45', requirement: 'RECOMMENDED' },
  { value: 'half_body', labelKey: 'step5.photoAngles.half_body', requirement: 'OPTIONAL' },
  { value: 'full_body', labelKey: 'step5.photoAngles.full_body', requirement: 'OPTIONAL' },
]

const REQUIREMENT_LABEL_KEYS: Record<'REQUIRED' | 'RECOMMENDED' | 'OPTIONAL', string> = {
  REQUIRED: 'step5.requirements.REQUIRED',
  RECOMMENDED: 'common.recommended',
  OPTIONAL: 'step5.requirements.OPTIONAL',
}

function asText(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}
function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && v.length > 0) : []
}

/** Soft heuristic only — task spec confirmed STEP 2's `ip_type` is a
 * free-text AI-generated candidate label (`virtual_ip_type_recommendation_service`),
 * not a fixed enum with a canonical "real person" boolean. This never
 * forces a mode or marks anything "不适用"; it only nudges which mode
 * card shows the t('common.recommended') badge — the user always retains the final
 * choice of all 4 source modes. */
function isLikelyRealPersonIp(profile: Record<string, unknown> | null): boolean {
  if (!profile) return false
  const haystack = [
    asText(profile.selected_ip_type_display_name),
    asText(profile.selected_ip_type),
    ...asStringArray(profile.tags),
  ]
    .join(' ')
    .toLowerCase()
  return /真人|real[\s_-]?person|真人IP|真人克隆/i.test(haystack)
}

function copyReferenceAssets(items: VirtualIpCharacterReferenceAsset[]): VirtualIpCharacterReferenceAsset[] {
  return items.map((i) => ({ ...i }))
}

/** Product-safe tenant messaging for AI execution readiness — never
 * surfaces worker ids, capability names, or internal infra counts. */
function aiExecutionBannerCopy(
  readiness: VirtualIpCharacterContext['readiness'],
  t: (key: string) => string,
): { message: string; description: string } {
  if (readiness.ai_execution_ready) {
    return {
      message: readiness.ai_execution_message || t('step5.aiReady'),
      description: '',
    }
  }
  return {
    message: t('step5.aiNotReady'),
    description: readiness.ai_execution_message || t('step5.aiNotReady'),
  }
}

export default function VirtualIpStep5Page() {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const { workspaceId = '', projectId = '' } = useParams()
  const navigate = useNavigate()
  const { primarySite } = useWorkspaceState()
  const siteId = primarySite?.id ?? ''

  const [pageState, setPageState] = useState<PageState>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [draft, setDraft] = useState<VirtualIpDraft | null>(null)
  const [context, setContext] = useState<VirtualIpCharacterContext | null>(null)
  const [batch, setBatch] = useState<VirtualIpCharacterBatch | null>(null)

  const [sourceMode, setSourceMode] = useState<VirtualIpCharacterSourceMode>('AI_FROM_POSITIONING')
  const [referenceAssets, setReferenceAssets] = useState<VirtualIpCharacterReferenceAsset[]>([])
  const [existingAssetId, setExistingAssetId] = useState<string | null>(null)
  const [realPersonPhotos, setRealPersonPhotos] = useState<VirtualIpRealPersonPhoto[]>([])
  const [visualPreferences, setVisualPreferences] = useState('')
  const [avoidances, setAvoidances] = useState('')
  const [generationCount, setGenerationCount] = useState(4)

  const [savingDraft, setSavingDraft] = useState(false)
  const [authorizing, setAuthorizing] = useState(false)
  const [dispatching, setDispatching] = useState(false)
  const [retrying, setRetrying] = useState(false)

  const initializedFormRef = useRef(false)
  const pollTimerRef = useRef<number | null>(null)

  const applyBatchToForm = useCallback((b: VirtualIpCharacterBatch | null) => {
    if (!b) return
    setSourceMode((b.source_mode as VirtualIpCharacterSourceMode) || 'AI_FROM_POSITIONING')
    setReferenceAssets(copyReferenceAssets(b.reference_assets || []))
    setExistingAssetId(b.existing_character_asset_id ?? null)
    setRealPersonPhotos((b.real_person_photos || []).map((p) => ({ ...p })))
    setVisualPreferences(b.visual_preferences ?? '')
    setAvoidances(b.avoidances ?? '')
    setGenerationCount(b.generation_count || 4)
  }, [])

  const load = useCallback(() => {
    if (!workspaceId) return
    setPageState('loading')
    setLoadError(null)
    getVirtualIpDraft(workspaceId, projectId)
      .then((dRes) => {
        setDraft(dRes.data)
        return getVirtualIpCharacterContext(workspaceId, dRes.data.id).then((cRes) => {
          setContext(cRes.data)
          setBatch(cRes.data.latest_batch)
          if (!initializedFormRef.current) {
            initializedFormRef.current = true
            if (cRes.data.latest_batch) {
              applyBatchToForm(cRes.data.latest_batch)
            } else if (isLikelyRealPersonIp(cRes.data.confirmed_profile)) {
              setSourceMode('REAL_PERSON_CLONE')
            }
          }
          if (!cRes.data.readiness.upstream_positioning_ready) {
            setPageState('upstream_not_ready')
          } else {
            setPageState('ready')
          }
        })
      })
      .catch((e) => {
        setLoadError((e as SaasApiError).message || t('common.loadFailedRefresh'))
        setPageState('error')
      })
  }, [workspaceId, applyBatchToForm])

  useEffect(() => {
    load()
  }, [load])

  // Poll the live batch while an execution is in flight — task spec
  // §STEP5 页面状态: "确保任务继续，即使用户离开页面也不受影响"; polling
  // here only re-reads server truth, it never re-triggers dispatch.
  useEffect(() => {
    if (!workspaceId || !draft || !batch) return
    if (!IN_FLIGHT_STATUSES.has(batch.status)) {
      if (pollTimerRef.current) {
        window.clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
      return
    }
    pollTimerRef.current = window.setInterval(() => {
      getVirtualIpCharacterBatch(workspaceId, draft.id, batch.generation_batch_id)
        .then((r) => setBatch(r.data))
        .catch(() => undefined)
    }, POLL_INTERVAL_MS)
    return () => {
      if (pollTimerRef.current) {
        window.clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, draft?.id, batch?.generation_batch_id, batch?.status])

  const goToStep4 = () => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-4`)
  const progress = useResolvedVirtualIpProgress()
  const { refreshProgress, selectionWorkspace } = useVirtualIpWorkflowContext()
  const canGoVoice =
    progress.completed_steps.includes(5) ||
    Boolean(selectionWorkspace?.selection?.selected_id && selectionWorkspace?.selection?.selected_kind)
  const goToStep6Voice = () => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-6`)

  const persistDraft = useCallback(
    (overrides?: Partial<VirtualIpCharacterBatch>): Promise<VirtualIpCharacterBatch> => {
      if (!draft) return Promise.reject(new Error('draft not loaded'))
      const body = {
        source_mode: overrides?.source_mode ?? sourceMode,
        reference_assets: (overrides?.reference_assets ?? referenceAssets).map((r) => ({
          asset_id: r.asset_id,
          reference_role: r.reference_role,
        })),
        existing_character_asset_id: overrides?.existing_character_asset_id ?? existingAssetId ?? null,
        real_person_mode: (overrides?.source_mode ?? sourceMode) === 'REAL_PERSON_CLONE',
        real_person_photos: (overrides?.real_person_photos ?? realPersonPhotos).map((p) => ({
          asset_id: p.asset_id,
          angle: p.angle,
        })),
        visual_preferences: overrides?.visual_preferences ?? (visualPreferences || null),
        avoidances: overrides?.avoidances ?? (avoidances || null),
        generation_count: overrides?.generation_count ?? generationCount,
      }
      return saveVirtualIpCharacterDraft(workspaceId, draft.id, body).then((r) => {
        setBatch(r.data)
        return r.data
      })
    },
    [workspaceId, draft, sourceMode, referenceAssets, existingAssetId, realPersonPhotos, visualPreferences, avoidances, generationCount],
  )

  const handleSaveDraft = () => {
    setSavingDraft(true)
    persistDraft()
      .then(() => message.success(t('step5.draftSaved')))
      .catch((e) => message.error((e as SaasApiError).message || t('common.saveFailed')))
      .finally(() => setSavingDraft(false))
  }

  const handleAuthorize = (b: VirtualIpCharacterBatch) => {
    setAuthorizing(true)
    authorizeVirtualIpRealPerson(workspaceId, draft!.id, b.generation_batch_id)
      .then((r) => {
        setBatch(r.data)
        message.success(t('step5.authorizationConfirmed'))
      })
      .catch((e) => message.error((e as SaasApiError).message || t('step5.authorizationFailed')))
      .finally(() => setAuthorizing(false))
  }

  const handleGenerate = () => {
    if (!draft) return
    setDispatching(true)
    persistDraft()
      .then((savedBatch) => dispatchVirtualIpCharacterBatch(workspaceId, draft.id, savedBatch.generation_batch_id))
      .then((r) => {
        setBatch(r.data)
        message.success(t('step5.dispatchSuccess'))
      })
      .catch((e) => message.error((e as SaasApiError).message || t('step5.dispatchFailed')))
      .finally(() => setDispatching(false))
  }

  const handleRetry = () => {
    if (!draft || !batch) return
    setRetrying(true)
    retryVirtualIpCharacterBatch(workspaceId, draft.id, batch.generation_batch_id)
      .then((r) => {
        setBatch(r.data)
        message.success(t('step5.retrySuccess'))
      })
      .catch((e) => message.error((e as SaasApiError).message || t('step5.retryFailed')))
      .finally(() => setRetrying(false))
  }

  if (pageState === 'loading') {
    return (
      <div className="tc-vip-page tc-vip-step5-page">
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 10 }} style={{ marginTop: 20 }} />
      </div>
    )
  }

  if (pageState === 'error') {
    return (
      <div className="tc-vip-page tc-vip-step5-page">
        <Alert type="error" showIcon message={t('common.loadFailed')} description={loadError} action={<Button size="small" onClick={load}>{t('actions:retry')}</Button>} />
      </div>
    )
  }

  if (pageState === 'upstream_not_ready') {
    return (
      <div className="tc-vip-page tc-vip-step5-page" data-testid="virtual-ip-step5-upstream-not-ready">
        <Step5TopBar onBack={goToStep4} canGoVoice={canGoVoice} onGoVoice={goToStep6Voice} staleReason={progress.step_state_reasons[5]} />
        <VirtualIpStepper currentStep={5} progress={progress} />
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message={t('step5.prerequisiteMessage')} description={context?.readiness.upstream_not_ready_reason || t('step5.prerequisiteDefault')}
          action={
            <Button type="primary" size="small" onClick={goToStep4}>
              {t('step5.backToPositioning')}
            </Button>
          }
          data-testid="vip-step5-upstream-not-ready-banner"
        />
      </div>
    )
  }

  const readiness = context!.readiness
  const profile = context!.confirmed_profile || {}
  const isRealPersonLikely = isLikelyRealPersonIp(profile)
  const isBusy = batch ? IN_FLIGHT_STATUSES.has(batch.status) : false
  const isSucceeded = batch?.status === 'SUCCEEDED'
  const needsAuthorization = sourceMode === 'REAL_PERSON_CLONE' && !batch?.real_person_authorization_confirmed_at
  const canEditForm = !isBusy && !isSucceeded

  let generateDisabledReason = ''
  if (isBusy) generateDisabledReason = t('step5.batchGenerating')
  else if (isSucceeded) generateDisabledReason = ''
  else if (needsAuthorization) generateDisabledReason = t('step5.authorizationRequired')
  else if (!readiness.ai_execution_ready) generateDisabledReason = readiness.ai_execution_message || t('step5.aiNotReady')
  const generateDisabled = Boolean(generateDisabledReason) && !isSucceeded

  return (
    <div className="tc-vip-page tc-vip-step5-page" data-testid="virtual-ip-step5-page">
      <Step5TopBar onBack={goToStep4} canGoVoice={canGoVoice} onGoVoice={goToStep6Voice} staleReason={progress.step_state_reasons[5]} />
      <VirtualIpStepper currentStep={5} progress={progress} />

      {!readiness.ai_execution_ready && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningFilled />}
          message={aiExecutionBannerCopy(readiness, t).message}
          description={aiExecutionBannerCopy(readiness, t).description}
          data-testid="vip-step5-ai-not-ready-banner"
          style={{ marginBottom: 4 }}
        />
      )}

      <div className="tc-vip-layout tc-vip-step5-layout">
        <div className="tc-vip-main">
          <IpContextCard profile={profile} />

          <SourceModeCard
            sourceMode={sourceMode}
            onChange={setSourceMode}
            isRealPersonLikely={isRealPersonLikely}
            disabled={!canEditForm}
          />

          {sourceMode === 'REFERENCE_IMAGES' && (
            <ReferenceImagesCard
              workspaceId={workspaceId}
              siteId={siteId}
              items={referenceAssets}
              onChange={setReferenceAssets}
              disabled={!canEditForm}
            />
          )}

          {sourceMode === 'EXISTING_IP_ASSET' && (
            <div className="tc-data-card tc-vip-card" data-testid="vip-step5-existing-asset-card">
              <div className="tc-vip-card-title">{t('step5.selectExistingAssetTitle')}</div>
              <p className="tc-vip-card-desc">{t('step5.selectExistingAssetDesc')}</p>
              {siteId ? (
                <ImageAssetPickerField
                  workspaceId={workspaceId}
                  siteId={siteId}
                  assetId={existingAssetId}
                  onChange={setExistingAssetId}
                  purpose="generic"
                  aspectRatio="1 / 1"
                  label={existingAssetId ? t('step5.changeAsset') : t('step5.selectAsset')}
                  compact
                />
              ) : (
                <Empty description={t('common.noSiteAvailable')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          )}

          {sourceMode === 'REAL_PERSON_CLONE' && (
            <RealPersonCard
              workspaceId={workspaceId}
              siteId={siteId}
              photos={realPersonPhotos}
              onChange={setRealPersonPhotos}
              disabled={!canEditForm}
              authorizedAt={batch?.real_person_authorization_confirmed_at ?? null}
              authorizing={authorizing}
              onAuthorize={() => {
                if (batch) {
                  handleAuthorize(batch)
                } else {
                  setSavingDraft(true)
                  persistDraft({ source_mode: 'REAL_PERSON_CLONE' })
                    .then((b) => handleAuthorize(b))
                    .catch((e) => message.error((e as SaasApiError).message || t('common.saveFailed')))
                    .finally(() => setSavingDraft(false))
                }
              }}
            />
          )}

          <PreferencesCard
            visualPreferences={visualPreferences}
            avoidances={avoidances}
            generationCount={generationCount}
            onVisualPreferencesChange={setVisualPreferences}
            onAvoidancesChange={setAvoidances}
            onGenerationCountChange={setGenerationCount}
            disabled={!canEditForm}
          />

          <GenerationStatusCard
            batch={batch}
            dispatching={dispatching}
            retrying={retrying}
            generateDisabled={generateDisabled}
            generateDisabledReason={generateDisabledReason}
            onGenerate={handleGenerate}
            onRetry={handleRetry}
            onSaveDraft={handleSaveDraft}
            savingDraft={savingDraft}
            canEditForm={canEditForm}
            aiExecutionReady={readiness.ai_execution_ready}
          />

          <VirtualIpCharacterWorkbench embedded onFactsChange={() => void refreshProgress()} />
        </div>

        <aside className="tc-vip-aside">
          <PositioningSummaryCard profile={profile} />
          <TipsCard />
          <HelpCard />
        </aside>
      </div>
    </div>
  )
}

function Step5TopBar({
  onBack,
  canGoVoice,
  onGoVoice,
  staleReason,
}: {
  onBack: () => void
  canGoVoice?: boolean
  onGoVoice?: () => void
  staleReason?: string
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const navigate = useNavigate()
  const { workspaceId = '' } = useParams()
  return (
    <div className="tc-vip-step4-topbar">
      <div>
        <Button className="tc-vip-step4-topbar-back" type="text" icon={<ArrowLeftOutlined />} onClick={onBack} data-testid="vip-step5-prev-button">
          {t('step5.backToPositioning')}
        </Button>
        <div className="tc-page-header tc-vip-step4-topbar-titles" style={{ marginBottom: 0 }}>
          <div>
            <h1 className="tc-page-title">{t('step5.pageTitle')}</h1>
            <div className="tc-vip-step-label">
              <span>STEP 5 / {VIRTUAL_IP_FLOW_TOTAL_STEPS}</span>
              <span className="tc-vip-step-title">{t('flow.steps.5.label')}</span>
            </div>
            <p className="tc-page-desc">{t('step5.description')}</p>
          </div>
        </div>
      </div>
      <div className="tc-vip-step4-topbar-actions">
        <Button size="small" type="text" onClick={() => navigate(`/workspace/${workspaceId}/overview`)}>
          {t('common.backToOverview')}
        </Button>
        {canGoVoice ? (
          <Button type="primary" onClick={onGoVoice} data-testid="vip-step5-next-voice-button">
            {t('step5.nextVoiceArrow')}
          </Button>
        ) : (
          <Tooltip title={staleReason || t('step5.selectCharacterFirst')}>
            <span>
              <Button type="primary" disabled data-testid="vip-step5-next-voice-button">
                {t('step5.nextVoiceArrow')}
              </Button>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

function IpContextCard({ profile }: { profile: Record<string, unknown> }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const ipTypeDisplay = asText(profile.selected_ip_type_display_name) || asText(profile.selected_ip_type)
  const name = asText(profile.name)
  const positioningLabel = asText(profile.positioning_label)
  const oneLiner = asText(profile.one_liner)
  const visualStyle = asText(profile.visual_style)
  const personaTraits = asStringArray(profile.persona_traits)

  return (
    <div className="tc-data-card tc-vip-card tc-vip-step5-context-card" data-testid="vip-step5-ip-context-card">
      <div className="tc-vip-card-head">
        <div className="tc-vip-card-title">
          <AimOutlined style={{ color: 'var(--primary-active-text)', marginRight: 6 }} />
          {t('step5.currentIpContext')}
        </div>
        <Tag color="success">{t('step4.confirmSuccess')}</Tag>
      </div>
      <div className="tc-vip-step5-context-grid">
        {ipTypeDisplay && <ContextBlock label={t('flow.steps.2.label')} value={ipTypeDisplay} />}
        {name && <ContextBlock label={t('flow.steps.3.label')} value={`${name}${positioningLabel ? `「${positioningLabel}」` : ''}`} />}
        {visualStyle && <ContextBlock label={t('fields.visualDirection')} value={visualStyle} />}
      </div>
      {oneLiner && <p className="tc-vip-step5-context-oneliner">「{oneLiner}」</p>}
      {personaTraits.length > 0 && (
        <div className="tc-vip-step4-chip-row">
          {personaTraits.map((t, i) => (
            <Tag key={i} className="tc-vip-analysis-tag">
              {t}
            </Tag>
          ))}
        </div>
      )}
    </div>
  )
}

function ContextBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="tc-vip-step4-quick-block">
      <span className="tc-vip-step4-quick-label">{label}</span>
      <div className="tc-vip-step4-quick-value">{value}</div>
    </div>
  )
}

const SOURCE_MODE_META: { value: VirtualIpCharacterSourceMode; labelKey: string; descKey: string; icon: ReactNode }[] = [
  { value: 'AI_FROM_POSITIONING', labelKey: 'step5.modes.AI_FROM_POSITIONING.label', descKey: 'step5.modes.AI_FROM_POSITIONING.desc', icon: <RobotOutlined /> },
  { value: 'REFERENCE_IMAGES', labelKey: 'step5.modes.REFERENCE_IMAGES.label', descKey: 'step5.modes.REFERENCE_IMAGES.desc', icon: <FileImageOutlined /> },
  { value: 'EXISTING_IP_ASSET', labelKey: 'step5.modes.EXISTING_IP_ASSET.label', descKey: 'step5.modes.EXISTING_IP_ASSET.desc', icon: <PictureOutlined /> },
  { value: 'REAL_PERSON_CLONE', labelKey: 'step5.modes.REAL_PERSON_CLONE.label', descKey: 'step5.modes.REAL_PERSON_CLONE.desc', icon: <UserOutlined /> },
]

function SourceModeCard({
  sourceMode,
  onChange,
  isRealPersonLikely,
  disabled,
}: {
  sourceMode: VirtualIpCharacterSourceMode
  onChange: (mode: VirtualIpCharacterSourceMode) => void
  isRealPersonLikely: boolean
  disabled: boolean
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const recommended: VirtualIpCharacterSourceMode = isRealPersonLikely ? 'REAL_PERSON_CLONE' : 'AI_FROM_POSITIONING'
  return (
    <div className="tc-data-card tc-vip-card" data-testid="vip-step5-source-mode-card">
      <div className="tc-vip-card-title">{t('step5.sourceTitle')}</div>
      <p className="tc-vip-card-desc">{t('step5.sourceDesc')}</p>
      <div className="tc-vip-step5-mode-grid">
        {SOURCE_MODE_META.map((m) => {
          const isSelected = sourceMode === m.value
          const isRecommended = m.value === recommended
          return (
            <button
              key={m.value}
              type="button"
              className={`tc-vip-step5-mode-card ${isSelected ? 'is-selected' : ''}`}
              disabled={disabled}
              onClick={() => onChange(m.value)}
              data-testid={`vip-step5-mode-${m.value}`}
            >
              {isSelected && (
                <span className="tc-vip-proposal-card-check">
                  <CheckCircleFilled />
                </span>
              )}
              <span className="tc-vip-step5-mode-icon">{m.icon}</span>
              <span className="tc-vip-step5-mode-label">{t(m.labelKey)}</span>
              {isRecommended && <span className="tc-vip-step5-mode-badge is-recommended">{t('common.recommended')}</span>}
              <span className="tc-vip-step5-mode-desc">{t(m.descKey)}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ReferenceImagesCard({
  workspaceId,
  siteId,
  items,
  onChange,
  disabled,
}: {
  workspaceId: string
  siteId: string
  items: VirtualIpCharacterReferenceAsset[]
  onChange: (items: VirtualIpCharacterReferenceAsset[]) => void
  disabled: boolean
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const [open, setOpen] = useState(false)
  return (
    <div className="tc-data-card tc-vip-card" data-testid="vip-step5-reference-images-card">
      <div className="tc-vip-card-head">
        <div>
          <div className="tc-vip-card-title">{t('step5.referenceImagesTitle')}</div>
          <p className="tc-vip-card-desc">{t('step5.referenceImagesDesc')}</p>
        </div>
        <Button size="small" icon={<PlusOutlined />} disabled={disabled} onClick={() => setOpen(true)} data-testid="vip-step5-add-reference-button">
          {t('step5.addReferenceImages')}
        </Button>
      </div>

      {items.length === 0 ? (
        <Empty description={t('step5.noReferenceImages')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div className="tc-vip-step5-reference-list">
          {items.map((item, idx) => (
            <div className="tc-vip-step5-reference-row" key={`${item.asset_id}-${idx}`}>
              {/* MEDIA_ELEMENT_CROSS_HOST_URL_AUDIT_AND_FIX_V1 — this <img>
                  never goes through saasApi's axios baseURL; a bare
                  `/api/saas/...` path 200'd with this frontend's own
                  index.html (text/html) on split-host Preview. */}
              <img src={resolveSaasMediaUrl(`/api/saas/workspaces/${workspaceId}/assets/${item.asset_id}/variants/thumbnail`) ?? undefined} alt="" className="tc-vip-step5-reference-thumb" />
              <Select
                size="small"
                value={item.reference_role}
                disabled={disabled}
                style={{ width: 160 }}
                options={REFERENCE_ROLE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
                onChange={(role) => {
                  const next = items.slice()
                  next[idx] = { ...next[idx], reference_role: role }
                  onChange(next)
                }}
              />
              <Button
                size="small"
                type="text"
                danger
                icon={<CloseCircleOutlined />}
                disabled={disabled}
                onClick={() => onChange(items.filter((_, i) => i !== idx))}
              />
            </div>
          ))}
        </div>
      )}

      {siteId && (
        <ImageAssetPicker
          open={open}
          workspaceId={workspaceId}
          siteId={siteId}
          purpose="generic"
          selectionMode="multiple"
          maxSelection={8}
          title={t('step5.addReferenceImages')} onCancel={() => setOpen(false)}
          onConfirm={(selections) => {
            const added: VirtualIpCharacterReferenceAsset[] = selections.map((s) => ({ asset_id: s.assetId, reference_role: 'OTHER' }))
            const existingIds = new Set(items.map((i) => i.asset_id))
            onChange([...items, ...added.filter((a) => !existingIds.has(a.asset_id))])
            setOpen(false)
          }}
        />
      )}
    </div>
  )
}

function RealPersonCard({
  workspaceId,
  siteId,
  photos,
  onChange,
  disabled,
  authorizedAt,
  authorizing,
  onAuthorize,
}: {
  workspaceId: string
  siteId: string
  photos: VirtualIpRealPersonPhoto[]
  onChange: (photos: VirtualIpRealPersonPhoto[]) => void
  disabled: boolean
  authorizedAt: string | null
  authorizing: boolean
  onAuthorize: () => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const photoForAngle = (angle: string) => photos.find((p) => p.angle === angle)?.asset_id ?? null
  const setPhotoForAngle = (angle: VirtualIpRealPersonAngle, assetId: string | null) => {
    const next = photos.filter((p) => p.angle !== angle)
    if (assetId) next.push({ asset_id: assetId, angle })
    onChange(next)
  }

  return (
    <div className="tc-data-card tc-vip-card" data-testid="vip-step5-real-person-card">
      <div className="tc-vip-card-title">
        <UserOutlined style={{ marginRight: 6 }} />
        {t('step5.realPersonMaterials')}
      </div>
      <p className="tc-vip-card-desc">{t('step5.realPersonDesc')}</p>

      <div className="tc-vip-step5-realperson-grid">
        {REAL_PERSON_ANGLES.map((a) => (
          <div className="tc-vip-step5-realperson-slot" key={a.value} data-testid={`vip-step5-realperson-${a.value}`}>
            <div className="tc-vip-step5-realperson-slot-head">
              <span>{t(a.labelKey)}</span>
              <span className={`tc-vip-step5-requirement-badge is-${a.requirement.toLowerCase()}`}>{t(REQUIREMENT_LABEL_KEYS[a.requirement])}</span>
            </div>
            {siteId ? (
              <ImageAssetPickerField
                workspaceId={workspaceId}
                siteId={siteId}
                assetId={photoForAngle(a.value)}
                onChange={(id) => setPhotoForAngle(a.value, id)}
                purpose="generic"
                aspectRatio="3 / 4"
                compact
              />
            ) : (
              <Empty description={t('common.noSiteAvailable')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>
        ))}
      </div>

      <div className="tc-vip-step5-authorization" data-testid="vip-step5-authorization-block">
        <Checkbox checked={Boolean(authorizedAt)} disabled={Boolean(authorizedAt) || disabled} onChange={() => onAuthorize()}>
          <SafetyCertificateOutlined style={{ marginRight: 4 }} />
          {t('step5.authorizationCheckbox')}
        </Checkbox>
        {authorizing && <LoadingOutlined style={{ marginLeft: 8 }} />}
        {authorizedAt && (
          <div className="tc-vip-step5-authorization-confirmed">
            <CheckCircleFilled style={{ color: 'var(--success-text)', marginRight: 4 }} />
            {t('common.authorizedAt', { time: new Date(authorizedAt).toLocaleString(undefined, { hour12: false }) })}
          </div>
        )}
      </div>
    </div>
  )
}

function PreferencesCard({
  visualPreferences,
  avoidances,
  generationCount,
  onVisualPreferencesChange,
  onAvoidancesChange,
  onGenerationCountChange,
  disabled,
}: {
  visualPreferences: string
  avoidances: string
  generationCount: number
  onVisualPreferencesChange: (v: string) => void
  onAvoidancesChange: (v: string) => void
  onGenerationCountChange: (v: number) => void
  disabled: boolean
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  return (
    <div className="tc-data-card tc-vip-card" data-testid="vip-step5-preferences-card">
      <div className="tc-vip-card-title">{t('step5.preferencesTitle')}</div>
      <div className="tc-vip-subfield">
        <span className="tc-vip-subfield-title">{t('step5.visualPreference')}</span>
        <textarea
          className="tc-vip-step5-textarea"
          placeholder={t('step5.visualPreferencePlaceholder')}
          value={visualPreferences}
          disabled={disabled}
          rows={2}
          onChange={(e) => onVisualPreferencesChange(e.target.value)}
        />
      </div>
      <div className="tc-vip-subfield">
        <span className="tc-vip-subfield-title">{t('step5.avoidItems')}</span>
        <textarea
          className="tc-vip-step5-textarea"
          placeholder={t('step5.avoidItemsPlaceholder')}
          value={avoidances}
          disabled={disabled}
          rows={2}
          onChange={(e) => onAvoidancesChange(e.target.value)}
        />
      </div>
      <div className="tc-vip-step5-count-row">
        <span className="tc-vip-subfield-title">{t('step5.generationCount')}</span>
        <InputNumber min={1} max={6} value={generationCount} disabled={disabled} onChange={(v) => onGenerationCountChange(v ?? 4)} />
      </div>
    </div>
  )
}

const STATUS_ICONS: Record<string, ReactNode> = {
  DRAFT: <InfoCircleOutlined />,
  READY_TO_GENERATE: <InfoCircleOutlined />,
  QUEUED: <LoadingOutlined />,
  RUNNING: <LoadingOutlined />,
  WAITING_PROVIDER: <LoadingOutlined />,
  RESULT_RECEIVED: <LoadingOutlined />,
  SUCCEEDED: <CheckCircleFilled />,
  FAILED: <ExclamationCircleFilled />,
}

function GenerationStatusCard({
  batch,
  dispatching,
  retrying,
  generateDisabled,
  generateDisabledReason,
  onGenerate,
  onRetry,
  onSaveDraft,
  savingDraft,
  canEditForm,
  aiExecutionReady,
}: {
  batch: VirtualIpCharacterBatch | null
  dispatching: boolean
  retrying: boolean
  generateDisabled: boolean
  generateDisabledReason: string
  onGenerate: () => void
  onRetry: () => void
  onSaveDraft: () => void
  savingDraft: boolean
  canEditForm: boolean
  aiExecutionReady: boolean
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const isBusy = batch ? IN_FLIGHT_STATUSES.has(batch.status) : false
  const isSucceeded = batch?.status === 'SUCCEEDED'
  const isFailed = batch?.status === 'FAILED'

  return (
    <div className="tc-data-card tc-vip-card tc-vip-step5-generate-card" data-testid="vip-step5-generation-status-card">
      <div className="tc-vip-card-head">
        <div className="tc-vip-card-title">
          <ThunderboltOutlined style={{ marginRight: 6 }} />
          {t('step5.generateCharacterPlans')}
        </div>
        {batch && (
          <span className={`tc-vip-status-badge ${isSucceeded ? 'is-success' : isFailed ? 'is-error' : isBusy ? 'is-info' : 'is-neutral'}`}>
            {STATUS_ICONS[batch.status]} {batch.status_label}
          </span>
        )}
      </div>

      {isBusy && (
        <Alert
          type="info"
          showIcon
          icon={<LoadingOutlined />}
          message={t('step5.generatingCharacter')} description={t('step5.generatingCharacterDesc')}
          data-testid="vip-step5-inflight-banner"
        />
      )}

      {isFailed && (
        <Alert
          type="error"
          showIcon
          message={t('jobStatus.failed')} description={batch?.error_message || t('step5.generateFailedDefault')}
          data-testid="vip-step5-failed-banner"
        />
      )}

      {isSucceeded && batch && (
        <Alert
          type="success"
          showIcon
          message={t('step5.firstBatchReady')} description={t('step5.firstBatchReadyDesc')}
          data-testid="vip-step5-generation-complete-hint"
        />
      )}

      <div className="tc-vip-step5-generate-actions">
        {!isSucceeded && (
          <Button onClick={onSaveDraft} loading={savingDraft} disabled={!canEditForm} data-testid="vip-step5-save-draft-button">
            {t('common.saveAsDraft')}
          </Button>
        )}

        {isFailed ? (
          <Button type="primary" icon={<ReloadOutlined />} loading={retrying} onClick={onRetry} data-testid="vip-step5-retry-button">
            {t('step5.retryGenerate')}
          </Button>
        ) : isBusy ? (
          <Button type="primary" loading disabled data-testid="vip-step5-generate-button">
            {t('step5.generatingEllipsis')}
          </Button>
        ) : generateDisabled ? (
          <Tooltip title={generateDisabledReason}>
            <span>
              <Button type="primary" disabled data-testid="vip-step5-generate-button">
                {t('step5.generateCharacterPlans')} →
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button type="primary" loading={dispatching} onClick={onGenerate} data-testid="vip-step5-generate-button">
            {t('step5.generateCharacterPlans')} →
          </Button>
        )}
      </div>
      {!aiExecutionReady && !isSucceeded && !isBusy && !isFailed && (
        <p className="tc-vip-card-hint">{t('step5.aiEnvNotReady')}</p>
      )}
    </div>
  )
}

function PositioningSummaryCard({ profile }: { profile: Record<string, unknown> }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const name = asText(profile.name) || '—'
  const ipTypeDisplay = asText(profile.selected_ip_type_display_name) || asText(profile.selected_ip_type) || '—'
  const targetAudience = asText(profile.target_audience)
  const visualStyle = asText(profile.visual_style)

  return (
    <div className="tc-data-card tc-vip-card" data-testid="vip-step5-positioning-summary-card">
      <div className="tc-vip-card-title">
        <SmileOutlined style={{ marginRight: 6 }} />
        {t('step5.positioningSummary')}
      </div>
      <div className="tc-vip-analysis-dl">
        <dt>{t('flow.steps.2.label')}</dt>
        <dd>{ipTypeDisplay}</dd>
        <dt>{t('flow.steps.3.label')}</dt>
        <dd>{name}</dd>
        {targetAudience && (
          <>
            <dt>{t('fields.targetAudience')}</dt>
            <dd>{targetAudience}</dd>
          </>
        )}
        {visualStyle && (
          <>
            <dt>{t('fields.visualDirection')}</dt>
            <dd>{visualStyle}</dd>
          </>
        )}
      </div>
    </div>
  )
}

function TipsCard() {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  return (
    <div className="tc-data-card tc-vip-card" data-testid="vip-step5-tips-card">
      <div className="tc-vip-card-title">
        <CameraOutlined style={{ marginRight: 6 }} />
        {t('step5.tipsTitle')}
      </div>
      <ul className="tc-vip-tips-list">
        <li>{t('step5.tip1')}</li>
        <li>{t('step5.tip2')}</li>
        <li>{t('step5.tip3')}</li>
        <li>{t('step5.tip4')}</li>
      </ul>
    </div>
  )
}

function HelpCard() {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  return (
    <div className="tc-data-card tc-vip-card tc-vip-help-block" data-testid="vip-step5-help-card">
      <div className="tc-vip-card-title">{t('step5.helpTitle')}</div>
      <div className="tc-vip-step4-help-block">
        <RobotOutlined className="tc-vip-step4-help-illustration" style={{ fontSize: 22 }} />
        <p className="tc-vip-step4-help-text">
          {t('step5.helpText')}
        </p>
      </div>
    </div>
  )
}
